import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from 'lucide-react';
import Reveal from '../components/Reveal';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s-()]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Phone number is invalid';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = validateForm();

    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);

      setTimeout(() => {
        setIsSubmitting(false);
        setSubmitSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });

        setTimeout(() => {
          setSubmitSuccess(false);
        }, 5000);
      }, 1500);
    } else {
      setErrors(newErrors);
    }
  };

  const contactInfo = [
    {
      icon: <MapPin className="text-crimson-600" size={26} />,
      title: 'Visit Us',
      details: ['391, Kolsa Lane', 'Off M. G. Road, Camp', 'Pune - 411001'],
      link: null
    },
    {
      icon: <Phone className="text-crimson-600" size={26} />,
      title: 'Call Us',
      details: ['Huzefa: +91 9359370652', 'Taher: +91 9890097573'],
      link: 'tel:+919359370652'
    },
    {
      icon: <Mail className="text-crimson-600" size={26} />,
      title: 'Email Us',
      details: ['indiaglassworks@gmail.com'],
      link: 'mailto:indiaglassworks@gmail.com'
    },
    {
      icon: <Clock className="text-crimson-600" size={26} />,
      title: 'Working Hours',
      details: ['Monday - Saturday', '9:00 AM - 7:00 PM'],
      link: null
    }
  ];

  return (
    <div className="pt-16 bg-cream-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-crimson-500 via-crimson-600 to-crimson-700 text-white py-20 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-64 h-64 bg-gold-300/20 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h1 className="font-display text-5xl md:text-6xl font-bold mb-6 animate-fadeInUp">
            Get In Touch
          </h1>
          <div className="w-24 h-1 bg-gold-300 mx-auto mb-6"></div>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto animate-fadeInUp text-cream-100/90" style={{ animationDelay: '0.2s' }}>
            Have a question or ready to start your project? We'd love to hear from you!
          </p>
        </div>
      </div>

      {/* Contact Info Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 mb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {contactInfo.map((info, index) => (
            <Reveal key={index} delay={index * 100}>
              <div className="frame-corners bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gold-100">
                <div className="flex justify-center mb-4">
                  {info.icon}
                </div>
                <h3 className="text-xl font-display font-bold text-center mb-3 text-ink-700">
                  {info.title}
                </h3>
                <div className="text-center text-ink-500 space-y-1">
                  {info.details.map((detail, idx) => (
                    <p key={idx} className="text-sm">
                      {info.link && idx === 0 ? (
                        <a href={info.link} className="hover:text-crimson-600 transition-colors">
                          {detail}
                        </a>
                      ) : (
                        detail
                      )}
                    </p>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Contact Form and Map Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Reveal animation="left" className="bg-white p-8 rounded-xl shadow-lg border border-gold-100">
            <h2 className="text-3xl font-display font-bold mb-6 text-ink-700">Send Us a Message</h2>

            {submitSuccess && (
              <div className="bg-green-50 border border-green-300 text-green-800 px-4 py-3 rounded-lg mb-6 animate-fadeIn flex items-start gap-2">
                <CheckCircle2 size={20} className="mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">Success!</p>
                  <p className="text-sm">Your message has been sent. We'll get back to you soon!</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-ink-600 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border ${errors.name ? 'border-crimson-400' : 'border-gold-200'} rounded-lg focus:ring-2 focus:ring-crimson-500 focus:border-transparent transition-all bg-cream-50/50`}
                  placeholder="Enter your name"
                />
                {errors.name && (
                  <p className="text-crimson-600 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-ink-600 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border ${errors.email ? 'border-crimson-400' : 'border-gold-200'} rounded-lg focus:ring-2 focus:ring-crimson-500 focus:border-transparent transition-all bg-cream-50/50`}
                    placeholder="your@email.com"
                  />
                  {errors.email && (
                    <p className="text-crimson-600 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-ink-600 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border ${errors.phone ? 'border-crimson-400' : 'border-gold-200'} rounded-lg focus:ring-2 focus:ring-crimson-500 focus:border-transparent transition-all bg-cream-50/50`}
                    placeholder="+91 1234567890"
                  />
                  {errors.phone && (
                    <p className="text-crimson-600 text-sm mt-1">{errors.phone}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-semibold text-ink-600 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gold-200 rounded-lg focus:ring-2 focus:ring-crimson-500 focus:border-transparent transition-all bg-cream-50/50"
                  placeholder="What's this about?"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-ink-600 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  className={`w-full px-4 py-3 border ${errors.message ? 'border-crimson-400' : 'border-gold-200'} rounded-lg focus:ring-2 focus:ring-crimson-500 focus:border-transparent transition-all resize-none bg-cream-50/50`}
                  placeholder="Tell us about your project..."
                ></textarea>
                {errors.message && (
                  <p className="text-crimson-600 text-sm mt-1">{errors.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`glass-sheen w-full bg-gradient-to-r from-crimson-500 to-crimson-600 hover:from-crimson-600 hover:to-crimson-700 text-white font-semibold py-4 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center shadow-md shadow-crimson-500/30 ${
                  isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <Send className="ml-2" size={20} />
                  </>
                )}
              </button>
            </form>
          </Reveal>

          {/* Map Section */}
          <Reveal animation="right" className="space-y-6">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gold-100">
              <h2 className="text-3xl font-display font-bold mb-6 text-ink-700">Find Us Here</h2>
              <div className="rounded-lg overflow-hidden shadow-md h-96 border border-gold-100">
                <iframe
                  src="https://www.google.com/maps?q=India+Glass+Works+And+Frames,+Kolsa+Lane,+Pune&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="India Glass Works And Frames Location"
                ></iframe>
              </div>
              <div className="mt-6">
                <a
                  href="https://maps.app.goo.gl/4oTBmZnSdsuiPBTj8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-crimson-600 hover:text-crimson-700 font-semibold transition-colors"
                >
                  <MapPin size={20} className="mr-2" />
                  Open in Google Maps
                </a>
              </div>
            </div>

            {/* Quick Contact */}
            <div className="bg-gradient-to-br from-gold-400 to-gold-500 p-8 rounded-xl shadow-lg text-ink-700">
              <h3 className="text-2xl font-display font-bold mb-4">Quick Contact</h3>
              <p className="mb-6">Need immediate assistance? Give us a call!</p>
              <div className="space-y-4">
                <a
                  href="tel:+919359370652"
                  className="flex items-center space-x-3 bg-white/40 hover:bg-white/60 p-4 rounded-lg transition-all transform hover:scale-105"
                >
                  <Phone size={24} />
                  <div>
                    <div className="font-semibold">Huzefa</div>
                    <div className="text-sm">+91 9359370652</div>
                  </div>
                </a>
                <a
                  href="tel:+919890097573"
                  className="flex items-center space-x-3 bg-white/40 hover:bg-white/60 p-4 rounded-lg transition-all transform hover:scale-105"
                >
                  <Phone size={24} />
                  <div>
                    <div className="font-semibold">Taher</div>
                    <div className="text-sm">+91 9890097573</div>
                  </div>
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Business Hours Section */}
      <div className="bg-cream-100 py-16">
        <Reveal animation="scale" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-display font-bold mb-8 text-ink-700">Visit Our Showroom</h2>
          <div className="bg-white p-8 rounded-xl shadow-lg max-w-2xl mx-auto border border-gold-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-left">
                <h3 className="font-bold text-lg mb-2 text-crimson-600">Business Days</h3>
                <p className="text-ink-600">Monday - Saturday</p>
              </div>
              <div className="text-left">
                <h3 className="font-bold text-lg mb-2 text-crimson-600">Timing</h3>
                <p className="text-ink-600">9:00 AM - 7:00 PM</p>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-gold-100">
              <p className="text-ink-500">
                <span className="font-semibold text-crimson-600">Note:</span> Sunday - Closed
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default Contact;
